import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Loader2, Send, Moon, Ban, CheckCircle2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SERVICE_OPTIONS } from "@/lib/pricing";
import { trackWhatsAppClick } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Janela de atendimento noturno
const NIGHT_START = 19;
const NIGHT_END = 23;
const NIGHT_DEFAULT_SERVICE = "avulso-60";

const emptyForm = {
  tutor_name: "",
  tutor_phone: "",
  tutor_email: "",
  pet_name: "",
  pet_breed: "",
  service_type: "",
  date: "",
  time: "",
  notes: "",
};

const DURATIONS = {
  "avulso-30": 30, "avulso-60": 60,
  "sem-30-2": 30, "sem-30-3": 30, "sem-60-2": 60, "sem-60-3": 60,
  "men-30-2": 30, "men-30-3": 30, "men-60-2": 60, "men-60-3": 60,
};

function isNightNow() {
  const h = new Date().getHours();
  return h >= NIGHT_START && h < NIGHT_END;
}
function nowTimeString() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
function todayDateString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function toMin(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}
function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

export default function BookingForm() {
  const night = useMemo(() => isNightNow(), []);
  const [form, setForm] = useState(() =>
    night
      ? { ...emptyForm, service_type: NIGHT_DEFAULT_SERVICE, date: todayDateString(), time: nowTimeString() }
      : emptyForm
  );
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState([]);       // [{time, end_time, duration}]
  const [busyLoading, setBusyLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Recarrega ocupação toda vez que a data muda
  useEffect(() => {
    if (!form.date) { setBusy([]); return; }
    let cancelled = false;
    const fetchBusy = async () => {
      setBusyLoading(true);
      try {
        const { data } = await axios.get(`${API}/availability`, { params: { date: form.date } });
        if (!cancelled) setBusy(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setBusy([]);
      } finally {
        if (!cancelled) setBusyLoading(false);
      }
    };
    fetchBusy();
    return () => { cancelled = true; };
  }, [form.date]);

  // Detecta se o horário atual do form colide com algum bloco ocupado
  const conflict = useMemo(() => {
    if (!form.time || !form.service_type || busy.length === 0) return null;
    const dur = DURATIONS[form.service_type] || 60;
    const start = toMin(form.time);
    const end = start + dur;
    return busy.find((b) => overlaps(start, end, toMin(b.time), toMin(b.end_time))) || null;
  }, [form.time, form.service_type, busy]);

  const validateTime = (t) => {
    if (!t) return false;
    const asMin = toMin(t);
    return asMin >= NIGHT_START * 60 && asMin <= NIGHT_END * 60;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tutor_name || !form.tutor_phone || !form.pet_name || !form.service_type || !form.date || !form.time) {
      toast.error("Preencha os campos obrigatórios do agendamento.");
      return;
    }
    if (!validateTime(form.time)) {
      toast.error("Escolha um horário entre 19:00 e 23:00 (atendimento noturno).");
      return;
    }
    if (conflict) {
      toast.error(`Esse horário conflita com um passeio já marcado (${conflict.time}–${conflict.end_time}). Escolha outro.`);
      return;
    }
    const chosen = SERVICE_OPTIONS.find((s) => s.value === form.service_type);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        ...form,
        tutor_email: form.tutor_email || null,
        plan_label: chosen?.label || form.service_type,
      });
      toast.success("Agendamento enviado! Abrindo WhatsApp para confirmação…");
      // reset + reload availability for the picked day
      const picked = form.date;
      setForm(emptyForm);
      // refresh busy list (async, non-blocking)
      if (picked) axios.get(`${API}/availability`, { params: { date: picked } }).then(r => setBusy(r.data || []));
      trackWhatsAppClick("booking");
      setTimeout(() => window.open(data.whatsapp_url, "_blank", "noopener"), 400);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível enviar o agendamento. Tente novamente.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(44,110,73,0.10)] p-7 md:p-9 border border-brand-cream"
      data-testid="booking-form"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-2xl bg-brand-green/10 grid place-items-center text-brand-green">
          <CalendarClock size={22} />
        </div>
        <div>
          <h3 className="font-display text-2xl text-brand-green">Agendar um passeio</h3>
          <p className="text-sm text-brand-brownDark">Checamos a agenda em tempo real e enviamos direto pro Google Calendar da Gisele.</p>
        </div>
      </div>

      {night && (
        <div data-testid="night-preset-banner" className="mb-5 flex items-center gap-3 bg-brand-green/10 border border-brand-green/20 rounded-2xl px-4 py-3">
          <div className="w-10 h-10 rounded-xl bg-brand-green text-brand-cream grid place-items-center shrink-0">
            <Moon size={18} />
          </div>
          <div className="text-sm text-brand-green">
            <strong>Passeio noturno</strong> pré-selecionado — atendimento ativo agora, das 19h às 23h.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="tutor_name">Seu nome *</Label>
          <Input id="tutor_name" data-testid="input-tutor-name" value={form.tutor_name} onChange={set("tutor_name")} placeholder="Ex. Ana Souza" className="mt-1 rounded-2xl" />
        </div>
        <div>
          <Label htmlFor="tutor_phone">WhatsApp/Telefone *</Label>
          <Input id="tutor_phone" data-testid="input-tutor-phone" value={form.tutor_phone} onChange={set("tutor_phone")} placeholder="(13) 9 9999-9999" className="mt-1 rounded-2xl" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="tutor_email">E-mail (para receber confirmação)</Label>
          <Input id="tutor_email" type="email" data-testid="input-tutor-email" value={form.tutor_email} onChange={set("tutor_email")} placeholder="voce@email.com" className="mt-1 rounded-2xl" />
        </div>
        <div>
          <Label htmlFor="pet_name">Nome do pet *</Label>
          <Input id="pet_name" data-testid="input-pet-name" value={form.pet_name} onChange={set("pet_name")} placeholder="Ex. Thor" className="mt-1 rounded-2xl" />
        </div>
        <div>
          <Label htmlFor="pet_breed">Raça / porte</Label>
          <Input id="pet_breed" data-testid="input-pet-breed" value={form.pet_breed} onChange={set("pet_breed")} placeholder="Ex. Golden, médio" className="mt-1 rounded-2xl" />
        </div>
        <div className="md:col-span-2">
          <Label>Serviço *</Label>
          <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
            <SelectTrigger data-testid="select-service-type" className="mt-1 rounded-2xl">
              <SelectValue placeholder="Escolha o plano de passeio" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`service-option-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="date">Data preferida *</Label>
          <Input id="date" type="date" data-testid="input-date" value={form.date} onChange={set("date")} className="mt-1 rounded-2xl" />
        </div>
        <div>
          <Label htmlFor="time">Horário * <span className="text-brand-brown text-xs font-normal">(19:00–23:00)</span></Label>
          <Input id="time" type="time" data-testid="input-time" value={form.time} onChange={set("time")} min="19:00" max="23:00" step={900} className="mt-1 rounded-2xl" />
        </div>
      </div>

      {/* Disponibilidade do dia */}
      {form.date && (
        <div
          data-testid="availability-panel"
          className={
            "mt-5 rounded-2xl border p-4 text-sm " +
            (conflict
              ? "bg-red-50 border-red-200 text-red-800"
              : busy.length === 0
              ? "bg-brand-green/5 border-brand-green/20 text-brand-green"
              : "bg-brand-cream border-brand-cream text-brand-brownDark")
          }
        >
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="inline-flex items-center gap-2 font-bold">
              {busyLoading ? <RefreshCw size={16} className="animate-spin" /> :
                conflict ? <Ban size={16} /> :
                busy.length === 0 ? <CheckCircle2 size={16} /> : <CalendarClock size={16} />}
              {busyLoading
                ? "Consultando a agenda…"
                : conflict
                ? "Esse horário conflita com um passeio já marcado"
                : busy.length === 0
                ? "Nenhum passeio marcado nessa data — livre!"
                : `${busy.length} passeio(s) já agendado(s) nessa data`}
            </div>
          </div>
          {busy.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1">
              {busy.map((b, i) => {
                const isThis = conflict && conflict.time === b.time;
                return (
                  <span
                    key={i}
                    data-testid={`busy-slot-${b.time}`}
                    className={
                      "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold " +
                      (isThis
                        ? "bg-red-200 text-red-900"
                        : "bg-white border border-brand-brown/25 text-brand-brownDark")
                    }
                  >
                    <Ban size={12} /> {b.time}–{b.end_time}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-5">
        <Label htmlFor="notes">Observações (rotina, alergias, etc.)</Label>
        <Textarea id="notes" data-testid="input-notes" value={form.notes} onChange={set("notes")} className="mt-1 rounded-2xl" rows={3} />
      </div>

      <Button
        type="submit"
        disabled={loading || !!conflict}
        data-testid="submit-booking"
        className="mt-6 w-full rounded-full h-12 text-base font-bold bg-brand-green hover:bg-brand-greenDark text-brand-cream shadow-[0_10px_30px_rgba(44,110,73,0.32)] transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
      >
        {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Send size={18} className="mr-2" />}
        {loading ? "Enviando…" : conflict ? "Horário ocupado — escolha outro" : "Enviar e abrir WhatsApp"}
      </Button>

      <p className="text-xs text-brand-brownDark text-center mt-3">
        Atendimento noturno exclusivo — passeios entre 19:00 e 23:00. A confirmação chega no Google Agenda da Gisele.
      </p>
    </form>
  );
}
